import {
  AttributeInput,
  CartFragmentFragment,
} from '@data/shopify/storefront/types'
import { SanityProductVariantFragment } from '@data/sanity/queries/types/product'
import { getProductVariants } from '@lib/sanity/product'
import { AutomaticDiscount, Cart, LineItem, LineItemAttribute } from '@lib/cart'
import { Locale } from '@lib/language'
import { CartFragmentFragmentLine } from './graphql/cart'
import { parseShopifyGlobalId } from './client'

export const assetFilenameAttributeKey = 'asset_filename'
export const assetUrlAttributeKey = 'url'
export const cartIdAttributeKey = 'cart_id'
export const cartCompletedAttributeKey = 'cart_completed'
export const configurationIdAttributeKey = 'configuration_id'
export const imageAssetColorAttributeKey = 'image_asset_color'
export const imageAssetFileIdAttributeKey = 'image_asset_file_id'
export const imageAssetTitleAttributeKey = 'image_asset_title'
export const imageAssetUrlAttributeKey = 'image_asset_url'
export const logoAssetColorAttributeKey = 'logo_asset_color'
export const logoAssetFileIdAttributeKey = 'logo_asset_file_id'
export const logoAssetTitleAttributeKey = 'logo_asset_title'
export const logoAssetUrlAttributeKey = 'logo_asset_url'
export const partnerAdsClickIdAttributeKey = 'x-pa-pacid'
export const partnerAdsClickId2AttributeKey = 'x-pacid'
export const partnerAdsIdAttributeKey = 'x-pa-id'
export const productNameAttributeKey = 'product_name'
export const productVariantIdAttributeKey = 'product_variant_id'
export const shopIdAttributeKey = 'shop_id'
export const institutionIdAttributeKey = 'school_id'
export const institutionNameAttributeKey = 'school_name'
export const taxExemptAttributeKey = 'tax_exempt'
export const textAttributeKey = 'freetext'
export const textColorAttributeKey = 'freetext_color'
export const vatIdAttributeKey = 'vat_id'

export enum CartLineItemBooleanAttribute {
  NO = 'no',
  YES = 'yes',
}

/**
 * Gets attributes related to shop.
 */
export const getShopAttributes = (shopId: string) => {
  const attributes: AttributeInput[] = [
    {
      key: `_${shopIdAttributeKey}`,
      value: shopId,
    },
  ]

  return attributes
}

/**
 * Gets attributes related to product variant.
 */
export const getProductVariantAttributes = (
  productVariant: SanityProductVariantFragment,
) => {
  const attributes: AttributeInput[] = [
    {
      key: `_${productVariantIdAttributeKey}`,
      value: productVariant.id.toString(),
    },
  ]

  return attributes
}

/**
 * Gets cart attributes related to taxes.
 */
export const getTaxCartAttributes = (vatId: string, taxExempt: boolean) => {
  const attributes: AttributeInput[] = []

  if (vatId) {
    // Set VAT ID checkout attribute
    attributes.push({
      key: `_${vatIdAttributeKey}`,
      value: vatId,
    })

    // Set tax exemption checkout attribute
    attributes.push({
      key: `_${taxExemptAttributeKey}`,
      value: taxExempt
        ? CartLineItemBooleanAttribute.YES
        : CartLineItemBooleanAttribute.NO,
    })
  }

  return attributes
}

/**
 * Parses Shopify line items.
 */
const parseShopifyCartLineItems = async (
  lineItems: CartFragmentFragmentLine[],
  locale: Locale,
) => {
  // Read product variants in cart
  const cartVariantIds = lineItems
    .map((shopifyCartLine) => {
      return shopifyCartLine.merchandise.id
        ? parseShopifyGlobalId(`${shopifyCartLine.merchandise.id}`)
        : null
    })
    .filter(Boolean) as number[]
  const productVariants = await getProductVariants(locale, cartVariantIds)

  return lineItems
    .map((shopifyCartLine) => {
      const variantId = shopifyCartLine.merchandise.id
        ? parseShopifyGlobalId(`${shopifyCartLine.merchandise.id}`)
        : null
      const variant = variantId
        ? productVariants.find(
            (productVariant) => productVariant.id === variantId,
          )
        : null

      if (!variant) {
        return
      }

      const lineItem: LineItem = {
        lineId: shopifyCartLine.id,
        merchandiseId: shopifyCartLine.merchandise.id,
        quantity: shopifyCartLine.quantity,
        attributes: shopifyCartLine.attributes?.map((attribute) => {
          const lineItemAttribute: LineItemAttribute = {
            key: attribute.key,
            value: attribute.value ?? '',
          }
          return lineItemAttribute
        }),
        ...variant,
      }

      return lineItem
    })
    .filter(Boolean) as LineItem[]
}

/**
 * Parses Shopify cart.
 */
export const parseShopifyCart = async (
  locale: Locale,
  shopifyCart?: CartFragmentFragment,
) => {
  if (!shopifyCart) {
    return
  }

  const lineItems = await parseShopifyCartLineItems(
    shopifyCart.lines.edges.map((edge) => edge.node),
    locale,
  )

  const subTotal = shopifyCart.estimatedCost.subtotalAmount
    ? Number(shopifyCart.estimatedCost.subtotalAmount.amount) * 100
    : 0
  const automaticDiscount = shopifyCart.lines.edges.reduce(
    (result, edge) => {
      const discountAllocation = edge.node.discountAllocations[0]

      if (
        discountAllocation &&
        'title' in discountAllocation &&
        'discountedAmount' in discountAllocation
      ) {
        return {
          title: discountAllocation.title,
          amount:
            result.amount +
            parseFloat(discountAllocation.discountedAmount.amount) * 100,
        }
      }

      return result
    },
    {
      title: '',
      amount: 0,
    } as AutomaticDiscount,
  )
  const total = subTotal - automaticDiscount.amount

  const cart: Cart = {
    id: shopifyCart.id,
    note: shopifyCart.note ?? '',
    lineItems,
    automaticDiscount,
    discountCodes: shopifyCart.discountCodes.map(
      (discountCode) => discountCode.code,
    ),
    subTotal,
    total,
    webUrl: `${shopifyCart.checkoutUrl}`,
  }

  return cart
}

/**
 * Gets whether cart is completed from cart attributes.
 */
export const getIsCartCompleted = (
  cartFragment: CartFragmentFragment | null,
) => {
  const cartCompletedAttribute = cartFragment?.attributes?.find(
    (attribute) => attribute.key === `_${cartCompletedAttributeKey}`,
  )
  return cartCompletedAttribute?.value === CartLineItemBooleanAttribute.YES
}
